/**
 * Super admin · Stream (`/admin/stream`): the moments to watch through the
 * day across every live workspace. Builds running now sit on top (the same
 * rows as Systems' "Stuck right now"), then builds starting and finishing,
 * plans ready, approved and waiting on a person, and PRs opening and
 * merging, newest first. Read from `runs`, `work_item_activity` and
 * `attempt_events` (`admin/stream.ts`), re-read every ten seconds while the
 * tab is visible. A line with a run opens the run sheet Systems uses.
 * YAGNI's own workspaces sit behind the Internal chip.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import type { SystemsStreamEvent, SystemsStreamKind, SystemsWorkerFace } from "@yagni/shared";
import { cx } from "../../queue/index.js";
import { useSheet } from "../../shell/useSheet.js";
import { useAuthSession } from "../../hooks/useWorkspaceRole.js";
import { useAdminScope } from "../../shell/AdminShell.js";
import { RunRowLine, RunSheet, Sec, dayLabel } from "./SystemsPage.js";
import { fetchSystemsStream, useSystemsStream } from "./systemsApi.js";
import "./systems.css";

const KINDS: ReadonlyArray<{ key: SystemsStreamKind; label: string }> = [
  { key: "build", label: "Builds" },
  { key: "plan", label: "Plans" },
  { key: "pr", label: "PRs" },
  { key: "failure", label: "Failures" },
];

const TONE: Record<SystemsStreamEvent["tone"], string> = {
  ok: "var(--success)",
  accent: "var(--accent)",
  warn: "var(--honesty)",
  bad: "var(--incident)",
};

function clockOf(iso: string): string {
  return iso.slice(11, 16);
}

function agoOf(iso: string | null, now: number): string {
  if (!iso) return "not yet";
  const s = Math.max(0, Math.round((now - Date.parse(iso)) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  return m < 60 ? `${m}m ago` : `${Math.floor(m / 60)}h ago`;
}

/** Ticks once a second so "updated 4s ago" stays true between reads. */
function useNow(): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);
  return now;
}

function StreamLine({ event, onOpen }: { event: SystemsStreamEvent; onOpen: (() => void) | null }) {
  const body = (
    <>
      <span className="tm">{clockOf(event.at)}</span>
      <span className="ev-dot" style={{ background: TONE[event.tone] }} aria-hidden />
      <span className="what">
        <b>{event.label}</b> {event.subject}
        {event.detail ? <span className="detail"> · {event.detail}</span> : null}
        <span className="where">
          {" "}
          · {event.workspaceName}
          {event.deskName ? ` › ${event.deskName}` : ""}
        </span>
      </span>
      <span className="src">{event.source}</span>
    </>
  );
  if (!onOpen) {
    return (
      <div className="ev" data-kind={event.kind}>
        {body}
      </div>
    );
  }
  return (
    <button type="button" className="ev open" data-kind={event.kind} onClick={onOpen}>
      {body}
    </button>
  );
}

export function StreamPage({
  workspaceId,
  onScope,
}: {
  workspaceId: string | null;
  onScope: (workspaceId: string | null) => void;
}) {
  const [off, setOff] = useState<Set<SystemsStreamKind>>(() => new Set());
  const [internal, setInternal] = useState(false);
  const kinds = KINDS.map((k) => k.key).filter((k) => !off.has(k));
  const query = { workspaceId, kinds: off.size === 0 ? [] : kinds, internal };
  const stream = useSystemsStream(query);
  const sheet = useSheet();
  const now = useNow();
  const data = stream.data;

  const [older, setOlder] = useState<SystemsStreamEvent[]>([]);
  const [olderBefore, setOlderBefore] = useState<string | null | undefined>(undefined);
  const [olderNote, setOlderNote] = useState<string | null>(null);
  const filterKey = `${workspaceId ?? ""}|${query.kinds.join(",")}|${internal}`;
  // A Load older that answers after the filter changed belongs to the old filter.
  const currentFilter = useRef(filterKey);
  currentFilter.current = filterKey;
  useEffect(() => {
    setOlder([]);
    setOlderBefore(undefined);
    setOlderNote(null);
  }, [filterKey]);

  const faces = useMemo(
    () =>
      new Map<string, SystemsWorkerFace>((data?.workers ?? []).map((face) => [face.slug, face])),
    [data?.workers],
  );
  const today = (data?.asOf ?? new Date(now).toISOString()).slice(0, 10);

  const events = useMemo(() => {
    const seen = new Set<string>();
    return [...(data?.events ?? []), ...older].filter((e) =>
      seen.has(e.id) ? false : (seen.add(e.id), true),
    );
  }, [data?.events, older]);
  const nextBefore = olderBefore === undefined ? (data?.nextBefore ?? null) : olderBefore;

  const days = useMemo(() => {
    const out: Array<{ day: string; events: SystemsStreamEvent[] }> = [];
    for (const event of events) {
      const day = event.at.slice(0, 10);
      const last = out[out.length - 1];
      if (last && last.day === day) last.events.push(event);
      else out.push({ day, events: [event] });
    }
    return out;
  }, [events]);

  const openRun = (runId: string) =>
    sheet.open(
      <RunSheet
        key={runId}
        runId={runId}
        faces={faces}
        today={today}
        onChanged={() => void stream.refresh()}
      />,
    );

  const loadOlder = async () => {
    if (!nextBefore) return;
    const asked = filterKey;
    try {
      const page = await fetchSystemsStream({ ...query, before: nextBefore });
      if (asked !== currentFilter.current) return;
      setOlder((was) => [...was, ...page.events]);
      setOlderBefore(page.nextBefore);
      setOlderNote(null);
    } catch (err) {
      if (asked !== currentFilter.current) return;
      setOlderNote(err instanceof Error ? err.message : "The older lines did not load");
    }
  };

  const toggle = (kind: SystemsStreamKind) =>
    setOff((was) => {
      const next = new Set(was);
      if (next.has(kind)) next.delete(kind);
      else if (next.size < KINDS.length - 1) next.add(kind);
      return next;
    });

  const live = data?.live ?? [];
  const nowMs = data ? Date.parse(data.asOf) : now;
  const scoped = data?.workspaces.find((w) => w.id === workspaceId);

  return (
    <div className="page systems stream-page" data-testid="admin-stream">
      <div className="qhd">
        <div className="top">
          <div className="title">
            <div className="label kick">Super admin</div>
            <h1>Stream</h1>
            <div className="sub">
              <span>
                <span
                  className="ldot"
                  style={{ display: "inline-block", verticalAlign: "middle", marginRight: 6 }}
                />
                {data ? `updated ${agoOf(data.asOf, now)}` : "reading every live workspace"} · every
                10s while this tab is open
              </span>
              {scoped && (
                <span>
                  scoped to <b>{scoped.name}</b>
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="rhythm" role="toolbar" aria-label="Filter by kind">
        {KINDS.map((k) => (
          <button
            key={k.key}
            type="button"
            className={cx("chip", !off.has(k.key) && "on")}
            aria-pressed={!off.has(k.key)}
            onClick={() => toggle(k.key)}
          >
            {k.label}
          </button>
        ))}
        <button
          type="button"
          className={cx("chip", internal && "on")}
          aria-pressed={internal}
          onClick={() => setInternal((v) => !v)}
        >
          Internal
        </button>
      </div>
      <div className="rhythm" role="toolbar" aria-label="Filter by workspace">
        <button
          type="button"
          className={cx("chip", !workspaceId && "on")}
          aria-pressed={!workspaceId}
          onClick={() => onScope(null)}
        >
          All
        </button>
        {(data?.workspaces ?? []).map((w) => (
          <button
            key={w.id}
            type="button"
            className={cx("chip", workspaceId === w.id && "on")}
            aria-pressed={workspaceId === w.id}
            onClick={() => onScope(workspaceId === w.id ? null : w.id)}
          >
            {w.name}
            {w.internal ? <span className="fact"> internal</span> : null}
          </button>
        ))}
      </div>
      {stream.error && <p className="status-line honest">The last read failed: {stream.error}</p>}

      <Sec
        title={`Building now · ${live.length}`}
        fact="every live run · quiet 10m or past 80% of budget is stuck"
        testId="stream-live"
      >
        {live.length === 0 ? (
          <div className="empty dashed tight">
            <p>{data ? "Nothing is running right now." : "Reading what is running."}</p>
          </div>
        ) : (
          <div className="rows">
            {live.map((run) => (
              <RunRowLine
                key={run.id}
                run={run}
                faces={faces}
                today={today}
                now={nowMs}
                tagMode="place"
                onOpen={() => openRun(run.id)}
              />
            ))}
          </div>
        )}
      </Sec>

      <Sec
        title="What happened"
        fact="newest first · UTC · open a line with a run for its sheet"
        testId="stream-feed"
      >
        {data && events.length === 0 ? (
          <div className="empty dashed tight">
            <p>Nothing has happened in the last 7 days for this filter.</p>
          </div>
        ) : (
          <div className="feed">
            {days.map((group) => (
              <div key={group.day} className="feedday">
                <div className="daybreak">{dayLabel(group.day, today)}</div>
                {group.events.map((event) => (
                  <StreamLine
                    key={event.id}
                    event={event}
                    onOpen={event.runId ? () => openRun(event.runId as string) : null}
                  />
                ))}
              </div>
            ))}
          </div>
        )}
        {olderNote && <p className="status-line honest">{olderNote}</p>}
        {nextBefore && (
          <div>
            <button type="button" className="btn secondary sm" onClick={() => void loadOlder()}>
              Load older
            </button>
          </div>
        )}
      </Sec>
    </div>
  );
}

/** The route: superadmins only, everyone else goes home. */
export function StreamRoute() {
  const session = useAuthSession();
  const [ws, setWs] = useAdminScope();
  if (!session?.user.isSuperadmin) return <Navigate to="/" replace />;
  return <StreamPage workspaceId={ws} onScope={setWs} />;
}
